import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import {
  Button,
  ButtonToolbar,
  Checkbox,
  DatePicker,
  FlexboxGrid,
  Form,
  SelectPicker,
} from "rsuite";

import "./MusicScheduleFilters.css";
import { updateIn } from "json-immut";
import { HelperServices } from "../../../core/services/HelperServices";
import { get } from "lodash/fp";
import { MusicScheduleListParams } from "./MusicScheduleDataTypes";
import {
  dateFormatObj,
  messageTypeArr,
  roleTypeArr,
  statusObjArr,
} from "../../../core/config/AppConstants";
import { useTranslation } from "react-i18next";
import { UtilityServices } from "../../../core/services/UtilityServices";
import { StoreServices } from "../../../core/services/StoreServices";
import { getItemFor, isEmptyVal } from "../../../utils/helpers";

export interface PropTypesInterface {
  onSubmit: Function;
  onClear: Function;
  tabViewSelected?: String;
}

export default function MusicScheduleFilters({
  onSubmit,
  onClear,
  tabViewSelected,
}: PropTypesInterface) {
  const { t } = useTranslation();
  const initSearchFormData = {
    name: "",
    company: "",
    store_group: "",
    is_default: "",
    new_schedule_everyday: "",
    start_date: null,
    end_date: null,
    period_nature: "",
    song_count: "",
    song_search: "",
    schedule_date: "",
    store: "",
    status: "",
    smart_search: "",
  };
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] =
    useState<MusicScheduleListParams>(initSearchFormData);
  const company = useSelector((state: any) => state.company);
  const [storeGroupList, setStoreGroupList] = useState([]);
  const [storeList, setStoreList] = useState([]);
  const [showFilter, setShowFilter] = useState(false);
  const handleSearchToggle = () => setShowFilter(!showFilter);

  function handleFormChanges(data: any) {
    let form = updateIn(formData, [data.path], data.value);
    setFormData(form);
  }

  function handleStoreChanges(value: any) {
    let form = formData;
    if (isEmptyVal(value)) {
      form = updateIn(form, ["store_group"], "");
      form = updateIn(form, ["store"], "");
    } else {
      form = updateIn(
        form,
        ["store_group"],
        getItemFor(value, "store_group_id", storeList)
      );
      form = updateIn(form, ["store"], value);
    }
    setFormData(form);
  }

  const handleDateChange = (date: any, frmIndex: any) => {
    let frmObj = { ...formData };
    frmObj[frmIndex] = date;
    setFormData({ ...frmObj });
  };

  const clearSearchDates = () => {
    formData.start_date = null;
    formData.end_date = null;
    setFormData({ ...formData });
  };

  async function init() {
    try {
    } catch (error) {
      console.log(error);
    }
  }

  useEffect(() => {
    init();
  }, []);

  useEffect(() => {}, [tabViewSelected]);

  useEffect(() => {
    storeGroupSearch("", company.id);
    storeSearch("", company.id);
  }, [company.id]);

  async function storeGroupSearch(searchGroup?: string, forComp?: number) {
    let compId: number = getCompanyId(forComp);

    if (!compId) {
      setStoreGroupList([]);
      return;
    }
    let params: any = { smart_search: searchGroup };
    if (compId) {
      params.company = compId;
      params.sort_by = "region_id";
      params.sort_order = "asc";
    }
    try {
      const res = await StoreServices.getStoreGroups(params);
      const respData = get("data.data", res);
      setStoreGroupList(respData);
    } catch (error: any) {
      UtilityServices.showToaster({
        message: error.message,
        type: messageTypeArr.error,
      });
    }
  }

  async function storeSearch(searchStore?: string, forComp?: number) {
    let compId: number = getCompanyId(forComp);

    if (!compId) {
      setStoreList([]);
      return;
    }
    let params: any = { smart_search: searchStore };
    if (compId) {
      params.company = compId;
      params.sort_by = "store_name";
      params.sort_order = "asc";
    }
    try {
      const res = await StoreServices.getStores(params);
      const respData = get("data.data", res);
      if (respData.length) {
        setStoreList(respData);
      }
    } catch (error: any) {
      UtilityServices.showToaster({
        message: error.message,
        type: messageTypeArr.error,
      });
    }
  }

  function getCompanyId(forComp?: number) {
    let compId: number = 0;
    if (UtilityServices.getLoggedInUser("user_type") == roleTypeArr.client) {
      compId = UtilityServices.getLoggedInUser("company_id");
    } else if (
      UtilityServices.getLoggedInUser("user_type") == roleTypeArr.backend
    ) {
      compId = forComp ? forComp : company.id;
    }
    return compId;
  }

  function clearSearch() {
    onClear();
    clearSearchDates();
    setFormData(initSearchFormData);
  }

  const handleDefaultScheduleCheck = (value: any, checked: any) => {
    let form = updateIn(formData, ["is_default"], checked ? value : "");
    setFormData(form);
  };

  const handleMultiScheduleCheck = (value: any, checked: any) => {
    let form = updateIn(
      formData,
      ["new_schedule_everyday"],
      checked ? value : ""
    );
    setFormData(form);
  };

  const handleEventCheck = (value: any, checked: any) => {
    let form = updateIn(formData, ["type"], checked ? value : "");
    setFormData(form);
  };

  return (
    <div>
      <div className="row mb-2">
        <ButtonToolbar className="mb-hide">
          <Button onClick={handleSearchToggle}>
            {t("MUSIC_SCHEDULE")} {t("ADVANCE_SEARCH")}
          </Button>
        </ButtonToolbar>
      </div>
      {showFilter && (
        <div className="content mb-3 pb-0 music-schedule-filter-panel">
          <Form
            formValue={formData}
            onChange={(data: any) => {
              let form = { ...formData, ...data };
              setFormData(form);
            }}
            className="music-schedule-filter-form"
          >
            <FlexboxGrid justify="space-between">
              <FlexboxGrid.Item>
                <Form.Group controlId="name">
                  <Form.ControlLabel>
                    {t("SCHEDULE")} {t("NAME")}
                  </Form.ControlLabel>
                  <Form.Control name="name" />
                </Form.Group>
              </FlexboxGrid.Item>
              <FlexboxGrid.Item>
                <Form.Group controlId="song_search">
                  <Form.ControlLabel>
                    {t("SONG")} {t("NAME")}
                  </Form.ControlLabel>
                  <Form.Control name="song_search" />
                </Form.Group>
              </FlexboxGrid.Item>
              <FlexboxGrid.Item>
                <Form.Group controlId="is_default">
                  <Form.ControlLabel>
                    {t("DEFAULT")} {t("SCHEDULE")}
                  </Form.ControlLabel>
                  <Checkbox
                    name="is_default"
                    value={1}
                    onChange={handleDefaultScheduleCheck}
                    checked={formData.is_default == "" ? false : true}
                  />
                </Form.Group>
              </FlexboxGrid.Item>
              <FlexboxGrid.Item>
                <Form.Group controlId="store_group">
                  <Form.ControlLabel>
                    {t("STORE")} {t("GROUP")}
                  </Form.ControlLabel>
                  <SelectPicker
                    data={storeGroupList}
                    onSearch={(e: any) => {
                      if (e) {
                        //storeGroupSearch(e);
                      }
                    }}
                    valueKey="id"
                    labelKey="region_group_name"
                    onSelect={(value, item) => {
                      handleFormChanges({ path: "store_group", value });
                    }}
                    onClean={(e: any) => {
                      handleFormChanges({ path: "store_group", value: "" });
                    }}
                    name="store_group"
                    value={formData?.store_group}
                  />
                </Form.Group>
              </FlexboxGrid.Item>
              <FlexboxGrid.Item>
                <Form.Group controlId="store">
                  <Form.ControlLabel>{t("STORE")}</Form.ControlLabel>
                  <SelectPicker
                    data={storeList}
                    onSearch={(e: any) => {
                      if (e) {
                        storeSearch(e);
                      }
                    }}
                    valueKey="id"
                    labelKey="store_name"
                    onSelect={(value, item) => {
                      handleStoreChanges(value);
                    }}
                    onClean={(e: any) => {
                      storeSearch("");
                      handleStoreChanges("");
                    }}
                    name="store"
                    value={formData?.store}
                  />
                </Form.Group>
              </FlexboxGrid.Item>
              <FlexboxGrid.Item>
                <Form.Group controlId="status">
                  <Form.ControlLabel>{t("STATUS")}</Form.ControlLabel>
                  <SelectPicker
                    data={statusObjArr}
                    valueKey="value"
                    labelKey="label"
                    onSelect={(value, item) => {
                      handleFormChanges({ path: "status", value });
                    }}
                    onClean={(e: any) => {
                      handleFormChanges({ path: "status", value: "" });
                    }}
                    name="status"
                    value={formData?.status}
                  />
                </Form.Group>
              </FlexboxGrid.Item>
              <FlexboxGrid.Item>
                <Form.Group controlId="new_schedule_everyday">
                  <Form.ControlLabel>
                    {t("NEW")} {t("SCHEDULE")} {t("EVERYDAY")}
                  </Form.ControlLabel>
                  <Checkbox
                    name="new_schedule_everyday"
                    value={1}
                    onChange={handleMultiScheduleCheck}
                    checked={
                      formData.new_schedule_everyday == "" ? false : true
                    }
                  />
                </Form.Group>
              </FlexboxGrid.Item>
            </FlexboxGrid>
            <FlexboxGrid justify="space-between">
              <FlexboxGrid.Item>
                <Form.Group controlId="start_date">
                  <Form.ControlLabel>
                    {t("START")} {t("DATE")}
                  </Form.ControlLabel>
                  <DatePicker
                    value={formData?.start_date}
                    format={dateFormatObj.calendar_d_m_y}
                    name="start_date"
                    id="start_date"
                    onChange={(e: any) => {
                      handleDateChange(e, "start_date");
                    }}
                  />
                </Form.Group>
              </FlexboxGrid.Item>
              <FlexboxGrid.Item>
                <Form.Group controlId="end_date">
                  <Form.ControlLabel>
                    {t("END")} {t("DATE")}
                  </Form.ControlLabel>
                  <DatePicker
                    value={formData?.end_date}
                    format={dateFormatObj.calendar_d_m_y}
                    name="end_date"
                    id="end_date"
                    onChange={(e: any) => {
                      handleDateChange(e, "end_date");
                    }}
                  />
                </Form.Group>
              </FlexboxGrid.Item>
            </FlexboxGrid>
            <div className="filter-btn music-schedule-filter-actions">
              <Button appearance="subtle" onClick={() => clearSearch()}>
                {t("CLEAR_FILTER")}
              </Button>
              <Button appearance="primary" onClick={() => onSubmit(formData)}>
                {t("SEARCH")}
              </Button>
            </div>
          </Form>
        </div>
      )}
    </div>
  );
}
